import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent, Avatar, Collapse, TablePagination, Popover, RadioGroup, FormControlLabel, Radio, useTheme, Stack } from '@mui/material';
import { FileDownloadOutlined as FileDownloadOutlinedIcon, FilterList as FilterListIcon, Visibility as VisibilityIcon, Clear as ClearIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, ErrorOutline as ErrorOutlineIcon, HourglassEmpty as HourglassEmptyIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';

const leaveTypeColors = {
    'Casual Leave': '#2196f3',
    'Sick Leave': '#f44336',
    'test leave': '#4caf50',
    'Earned Leave': '#9c27b0',
    'Maternity Leave': '#9c27b0',
    'Paternity Leave': '#673ab7',
};

const statusConfig = {
    'APPROVED': { color: '#4caf50', bgColor: '#e8f5e9', icon: <CheckCircleIcon fontSize="small" /> },
    'REJECTED': { color: '#f44336', bgColor: '#ffebee', icon: <CancelIcon fontSize="small" /> },
    'PENDING': { color: '#ff9800', bgColor: '#fff3e0', icon: <HourglassEmptyIcon fontSize="small" /> },
    'CANCELLED': { color: '#757575', bgColor: '#f5f5f5', icon: <ErrorOutlineIcon fontSize="small" /> }
};

// Helper Functions
const formatText = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const formatDateForAPI = (date) => date ? new Date(date).toISOString().split('T')[0] : '';

const EmployeesLeaveHistory = () => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;
    const theme = useTheme();

    // States
    const [leaveHistoryData, setLeaveHistoryData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);

    const [department, setDepartment] = useState('All');
    const [leaveType, setLeaveType] = useState('All');
    const [status, setStatus] = useState('All');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedLocationFilter, setSelectedLocationFilter] = useState('All');

    const [openFilters, setOpenFilters] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const [locations, setLocations] = useState([]);
    const [departmentsWithIds, setDepartmentsWithIds] = useState([]);
    const [leaveTypesWithIds, setLeaveTypesWithIds] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState('');
    const [pagination, setPagination] = useState({ page: 1, rowsPerPage: 10, totalCount: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchMetadata = async () => {
            if (!orgId) return;

            try {
                const deptUrl = typeof API_ENDPOINTS.fetchDepartmentsByOrganizationId === 'function'
                    ? API_ENDPOINTS.fetchDepartmentsByOrganizationId(orgId)
                    : `${API_ENDPOINTS.fetchDepartmentsByOrganizationId}/${orgId}`;

                const leaveUrl = typeof API_ENDPOINTS.fetchLeaveCountsForOrganization === 'function'
                    ? API_ENDPOINTS.fetchLeaveCountsForOrganization(orgId)
                    : `${API_ENDPOINTS.fetchLeaveCountsForOrganization}/${orgId}`;

                const locUrl = typeof API_ENDPOINTS.fetchLocationsByOrganizationId === 'function'
                    ? API_ENDPOINTS.fetchLocationsByOrganizationId(orgId)
                    : `${API_ENDPOINTS.fetchLocationsByOrganizationId}/${orgId}`;

                const [deptRes, leaveRes, locRes] = await Promise.all([
                    getData(deptUrl).catch(err => ({ status: 'ERROR', message: err })),
                    getData(leaveUrl).catch(err => ({ status: 'ERROR', message: err })),
                    getData(locUrl).catch(err => ({ status: 'ERROR', message: err }))
                ]);

                if (deptRes.status === 'SUCCESS') {
                    setDepartmentsWithIds(deptRes.message?.departments || deptRes.result || []);
                }
                if (leaveRes.status === 'SUCCESS') {
                    setLeaveTypesWithIds(leaveRes.message?.leaveTypes || leaveRes.result || []);
                }
                if (locRes.status === 'SUCCESS') {
                    setLocations(locRes.message || locRes.result || []);
                }

            } catch (err) {
                return null;
            }
        };
        fetchMetadata();
    }, [orgId]);

    // Search Debounce
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
        return () => clearTimeout(searchTimeoutRef.current);
    }, [searchTerm]);

    const fetchLeaveHistory = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            // Derive the IDs here so the URLSearchParams logic is clean
            const deptId = departmentsWithIds.find(d => d.department === department)?.id;
            const ltId = leaveTypesWithIds.find(l => l.leave_type === leaveType)?.id;
            const locId = locations.find(l => l.location_name === selectedLocationFilter)?.id;

            const params = new URLSearchParams({
                organization_id: orgId,
                page: pagination.page,
                limit: pagination.rowsPerPage
            });

            if (debouncedSearchTerm) params.append('name', debouncedSearchTerm);
            if (status !== 'All') params.append('status', status);
            if (startDate) params.append('start_date', formatDateForAPI(startDate));
            if (endDate) params.append('end_date', formatDateForAPI(endDate));
            if (deptId) params.append('department_id', deptId);
            if (ltId) params.append('leave_type_id', ltId);
            if (locId) params.append('location_id', locId);

            const response = await getData(`${API_ENDPOINTS.getAllLeaveHistoryByOrganizationId}?${params.toString()}`);
            if (response.status === 'SUCCESS') {
                setLeaveHistoryData(response.result.data || []);
                setPagination(prev => ({ ...prev, totalCount: response.result.pagination?.totalRecords || 0 }));
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }, [orgId, pagination.page, pagination.rowsPerPage, debouncedSearchTerm, department, leaveType, status, startDate, endDate, selectedLocationFilter, departmentsWithIds, leaveTypesWithIds, locations]);

    useEffect(() => { fetchLeaveHistory(); }, [fetchLeaveHistory]);

    const handleDownload = async () => {
        try {
            const deptId = departmentsWithIds.find(d => d.department === department)?.id || 'All';
            const locId = locations.find(l => l.location_name === selectedLocationFilter)?.id || 'All';
            const ltId = leaveTypesWithIds.find(l => l.leave_type === leaveType)?.id || 'All';

            // FIX: Invoke the function with () to get the string path
            const path = downloadFormat === 'pdf'
                ? API_ENDPOINTS.downloadLeaveHistoryPDF({})
                : API_ENDPOINTS.downloadLeaveHistoryExcel({});

            // Constructing params to match your "actual url" example
            const queryParams = new URLSearchParams({
                organization_id: orgId,
                department_id: deptId,
                location_id: locId,
                leave_type_id: ltId,
                status: status,
                from_date: startDate ? formatDateForAPI(startDate) : '',
                to_date: endDate ? formatDateForAPI(endDate) : ''
            });

            const finalUrl = `${path}?${queryParams.toString()}`;

            const response = await getData(finalUrl, { responseType: 'blob' });
            const blob = new Blob([response], {
                type: downloadFormat ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute('download', `Leave_Report_${new Date().getTime()}.${downloadFormat === 'excel' ? 'xlsx' : 'pdf'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setAnchorEl(null);
        } catch (error) {
        }
    };
    const clearFilters = () => {
        setDepartment('All'); setLeaveType('All'); setStatus('All');
        setStartDate(null); setEndDate(null); setSelectedLocationFilter('All');
        setSearchTerm(''); setPagination(prev => ({ ...prev, page: 1 }));
    };

    const getStatusChip = (status) => {
        const config = statusConfig[status?.toUpperCase()] || statusConfig['PENDING'];
        return <Chip size="small" label={formatText(status)} icon={config.icon} sx={{ color: config.color, bgcolor: config.bgColor, fontWeight: 500 }} />;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {loading && <Loader />}

                <Card elevation={0} sx={{ mb: 3, background: 'linear-gradient(135deg, #26c6da 0%, #00acc1 100%)', color: 'white' }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight={600}>Leave History</Typography>
                        <Typography variant="body2">Manage and export employee leave records</Typography>
                    </CardContent>
                </Card>

                {/* Search and Action Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">Employee Records</Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField size="small" placeholder="Search name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} color="primary"><FileDownloadOutlinedIcon /></IconButton>
                        <Button variant="contained" startIcon={<FilterListIcon />} onClick={() => setOpenFilters(!openFilters)}>Filters</Button>
                    </Stack>
                </Box>

                {/* Filters Section */}
                <Collapse in={openFilters}>
                    <Card sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Department</InputLabel>
                                    <Select value={department} label="Department" onChange={(e) => setDepartment(e.target.value)}>
                                        <MenuItem value="All">All Departments</MenuItem>
                                        {departmentsWithIds.map(d => <MenuItem key={d.id} value={d.department}>{d.department}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Branch</InputLabel>
                                    <Select value={selectedLocationFilter} label="Branch" onChange={(e) => setSelectedLocationFilter(e.target.value)}>
                                        <MenuItem value="All">All Branches</MenuItem>
                                        {locations.map(l => <MenuItem key={l.id} value={l.location_name}>{l.location_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <DatePicker
                                    label="From"
                                    value={startDate}
                                    onChange={setStartDate}
                                    // Apply size via slotProps on the DatePicker itself
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <DatePicker
                                    label="To"
                                    value={endDate}
                                    onChange={setEndDate}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                                        {['All', 'APPROVED', 'REJECTED', 'PENDING', 'CANCELLED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <Button variant="outlined" fullWidth startIcon={<ClearIcon />} onClick={clearFilters} sx={{ height: '40px' }}>Clear</Button>
                            </Grid>
                        </Grid>
                    </Card>
                </Collapse>

                {/* Table */}
                <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f5f7fa' }}>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Leave Type</TableCell>
                                    <TableCell>Period</TableCell>
                                    <TableCell align="center">Days</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {leaveHistoryData.map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 32, height: 32, fontSize: '0.8rem' }}>
                                                    {row.employee_name?.split(' ').map(n => n[0]).join('')}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>{row.employee_name}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{row.employee_id}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={row.leave_type} sx={{ bgcolor: `${leaveTypeColors[row.leave_type] || '#ccc'}20`, color: leaveTypeColors[row.leave_type] || '#666' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatDate(row.start_date)}</Typography>
                                            <Typography variant="caption" color="textSecondary">to {formatDate(row.end_date)}</Typography>
                                        </TableCell>
                                        <TableCell align="center">{row.leave_duration}</TableCell>
                                        <TableCell>{getStatusChip(row.status)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => { setSelectedLeave(row); setIsDetailsOpen(true); }}><VisibilityIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={pagination.totalCount}
                        rowsPerPage={pagination.rowsPerPage}
                        page={pagination.page - 1}
                        onPageChange={(_, newPage) => setPagination(p => ({ ...p, page: newPage + 1 }))}
                        onRowsPerPageChange={(e) => setPagination(p => ({ ...p, rowsPerPage: parseInt(e.target.value, 10), page: 1 }))}
                    />
                </Card>

                {/* Download Popover */}
                <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Format</Typography>
                        <RadioGroup value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
                            <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
                            <FormControlLabel value="excel" control={<Radio size="small" />} label="Excel" />
                        </RadioGroup>
                        <Button fullWidth variant="contained" size="small" sx={{ mt: 1 }} disabled={!downloadFormat} onClick={handleDownload}>Download</Button>
                    </Box>
                </Popover>

                {/* Details Dialog */}
                <Dialog open={isDetailsOpen} onClose={() => setIsDetailsOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>Leave Details</DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {selectedLeave && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}><Typography variant="caption">Employee</Typography><Typography variant="body1">{selectedLeave.employee_name}</Typography></Grid>
                                <Grid size={{ xs: 6 }}><Typography variant="caption">Leave Type</Typography><Typography variant="body1">{selectedLeave.leave_type}</Typography></Grid>
                                <Grid size={{ xs: 12 }}><Typography variant="caption">Reason</Typography><Typography variant="body1">{selectedLeave.leave_reason || 'N/A'}</Typography></Grid>
                                <Grid size={{ xs: 6 }}><Typography variant="caption">Applied On</Typography><Typography variant="body1">{formatDate(selectedLeave.createdAt)}</Typography></Grid>
                                <Grid size={{ xs: 6 }}><Typography variant="caption">Status</Typography><Box>{getStatusChip(selectedLeave.status)}</Box></Grid>
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions><Button onClick={() => setIsDetailsOpen(false)}>Close</Button></DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default EmployeesLeaveHistory;